export type RoomOrientation = "portrait" | "landscape";

export type CardScales = {
    hand: number;
    opponent: number;
    discard: number;
    deck: number;
};

type RoomBoardMetrics = {
    width: number;
    height: number;
    top: number;
};

export type RoomLayoutValue = {
    orientation: RoomOrientation;
    boardShape: "circle" | "portrait" | "landscape";
    scale: number;
    progress: number;
    scales: CardScales;
    board: RoomBoardMetrics;
    deckRotation: 0 | 90;
    alignDeckWithDiscard: boolean;
    deckCenterOffset?: number;
    activeHandBottom: number;
    inactiveHandBottom: number;
    opponentHandMarginTop: number;
    handFanMaxWidth?: number;
    discardSize: number;
    actionTooltipPlacement: "top" | "bottom";
};

const LANDSCAPE_REFERENCE = {
    width: 1366,
    height: 768,
    minScale: 0.6,
    maxScale: 1.1,
};

const PORTRAIT_REFERENCE = {
    width: 390,
    height: 844,
    minScale: 0.82,
    maxScale: 1.6,
};

const clamp = (value: number, min: number, max: number) =>
    Math.min(max, Math.max(min, value));

const progressBetween = (value: number, from: number, to: number) =>
    clamp((value - from) / (to - from), 0, 1);

const lerp = (from: number, to: number, progress: number) =>
    from + (to - from) * progress;

const interpolateScales = (
    from: CardScales,
    to: CardScales,
    progress: number
): CardScales => ({
    hand: lerp(from.hand, to.hand, progress),
    opponent: lerp(from.opponent, to.opponent, progress),
    discard: lerp(from.discard, to.discard, progress),
    deck: lerp(from.deck, to.deck, progress),
});

const resolveLandscapeLayout = (
    width: number,
    height: number
): RoomLayoutValue => {
    const scale = clamp(
        Math.min(
            width / LANDSCAPE_REFERENCE.width,
            height / LANDSCAPE_REFERENCE.height
        ),
        LANDSCAPE_REFERENCE.minScale,
        LANDSCAPE_REFERENCE.maxScale
    );
    const progress = progressBetween(scale, LANDSCAPE_REFERENCE.minScale, 1);
    const growth = progressBetween(scale, 1, LANDSCAPE_REFERENCE.maxScale);
    const referenceScales: CardScales = {
        hand: 1,
        opponent: 0.2,
        discard: 0.8,
        deck: 0.8,
    };

    return {
        orientation: "landscape",
        boardShape: "landscape",
        scale,
        progress,
        scales: interpolateScales(
            { hand: 0.77, opponent: 0.13, discard: 0.66, deck: 0.54 },
            referenceScales,
            progress
        ),
        board: {
            width: lerp(462, 745, progress) * lerp(1, 1.1, growth),
            height: lerp(222, 365, progress) * lerp(1, 1.1, growth),
            top: lerp(78, 166, progress) * lerp(1, 1.04, growth),
        },
        deckRotation: 0,
        alignDeckWithDiscard: true,
        deckCenterOffset: lerp(125, 147, progress) * lerp(1, 1.08, growth),
        activeHandBottom: lerp(-12, -34, progress),
        inactiveHandBottom: lerp(-38, -60, progress),
        opponentHandMarginTop: lerp(68, 80, progress) * lerp(1, 1.05, growth),
        handFanMaxWidth: lerp(420, 530, progress) * lerp(1, 1.08, growth),
        discardSize: lerp(129, 160, progress) * lerp(1, 1.1, growth),
        actionTooltipPlacement: scale < 0.85 ? "top" : "bottom",
    };
};

const resolvePortraitLayout = (
    width: number,
    height: number
): RoomLayoutValue => {
    const scale = clamp(
        Math.min(
            width / PORTRAIT_REFERENCE.width,
            height / PORTRAIT_REFERENCE.height
        ),
        PORTRAIT_REFERENCE.minScale,
        PORTRAIT_REFERENCE.maxScale
    );
    const compactProgress = progressBetween(scale, PORTRAIT_REFERENCE.minScale, 1);
    const growth = progressBetween(scale, 1, PORTRAIT_REFERENCE.maxScale);
    const useCircularBoard = width <= 360 && height <= 520;
    const compactScales = interpolateScales(
        { hand: 0.8, opponent: 0.15, discard: 0.73, deck: 0.45 },
        { hand: 0.9, opponent: 0.18, discard: 0.93, deck: 0.5 },
        compactProgress
    );
    const scales = useCircularBoard
        ? { hand: 0.75, opponent: 0.15, discard: 0.68, deck: 0.45 }
        : interpolateScales(
            compactScales,
            { hand: 1.04, opponent: 0.2, discard: 0.98, deck: 0.7 },
            growth
        );
    const minimumBoardWidth = lerp(208, 252, compactProgress);
    const boardWidth = clamp(width * 0.44, minimumBoardWidth, 440);
    const boardHeight = Math.min(
        boardWidth * (464 / 250),
        height * 0.67
    );
    const circularBoardSize = clamp(
        Math.min(width * 0.76, height * 0.54),
        220,
        250
    );
    const resolvedBoardWidth = useCircularBoard ? circularBoardSize : boardWidth;
    const resolvedBoardHeight = useCircularBoard ? circularBoardSize : boardHeight;

    return {
        orientation: "portrait",
        boardShape: useCircularBoard ? "circle" : "portrait",
        scale,
        progress: compactProgress,
        scales,
        board: {
            width: resolvedBoardWidth,
            height: resolvedBoardHeight,
            top: useCircularBoard
                ? clamp(height * 0.17, 78, 90)
                : clamp(height * 0.13, 90, 130),
        },
        deckRotation: 90,
        alignDeckWithDiscard: false,
        activeHandBottom: -34,
        inactiveHandBottom: -60,
        opponentHandMarginTop: lerp(68, 80, compactProgress) * lerp(1, 1.05, growth),
        discardSize: useCircularBoard
            ? clamp(circularBoardSize * 0.56, 120, 140)
            : clamp(boardWidth * 0.68, 128, 200),
        actionTooltipPlacement: "top",
    };
};

export const resolveRoomLayout = (width: number, height: number): RoomLayoutValue => {
    const orientation: RoomOrientation = width > height ? "landscape" : "portrait";

    return orientation === "landscape"
        ? resolveLandscapeLayout(width, height)
        : resolvePortraitLayout(width, height);
};

export const getRoomLayoutSnapshot = (): RoomLayoutValue =>
    resolveRoomLayout(window.innerWidth, window.innerHeight);
